import { useCallback, useEffect, useState } from 'react';
import useDragAndDrop from 'src/hooks/useDragAndDrop';
import { TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION } from '../constants';

interface IUseResizableNotesFormProps {
  containerResizable: HTMLDivElement | null;
  elementToDrag: HTMLDivElement | null;
  visible?: boolean;
}

interface IUseResizableNotesFormResult {
  flexGrow: number;
  isDragging: boolean;
  setIsMouseDownOnElementDrag: (value: boolean) => void;
  handleMouseMove: (event: React.MouseEvent<HTMLElement>) => void;
}

export const useResizableNotesForm = ({
  containerResizable,
  elementToDrag,
  visible = false,
}: IUseResizableNotesFormProps): IUseResizableNotesFormResult => {
  const { isDragging } = useDragAndDrop();
  const [isMouseDownOnElementDrag, setIsMouseDownOnElementDrag] = useState(false);
  const [flexGrow, setFlexGrow] = useState(0);
  const [topPositionContainerWhenVisible, setTopPositionContainerWhenVisible] = useState(0);

  useEffect(() => {
    setFlexGrow(visible ? 1 : 0);
  }, [visible]);

  useEffect(() => {
    if (!isDragging) {
      setIsMouseDownOnElementDrag(false);
    }
  }, [isDragging]);

  useEffect(() => {
    if (flexGrow < 1) {
      return;
    }
    const timeWaitCssTransition = setTimeout(() => {
      setTopPositionContainerWhenVisible(containerResizable?.getBoundingClientRect().top || 0);
    }, TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION);
    return () => {
      clearTimeout(timeWaitCssTransition);
    };
  }, [flexGrow, containerResizable]);

  const limitValueBetweenMaxAndMin = (value = 0, maxValue = 1, minValue = 0) => {
    if (value > maxValue) {
      return maxValue;
    }

    if (value < minValue) {
      return minValue;
    }

    return value;
  };

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const positionY = event.clientY;
      if (isMouseDownOnElementDrag && containerResizable && elementToDrag) {
        const containerBottom = containerResizable.getBoundingClientRect().bottom;
        const elementPositionTop = containerBottom - positionY;
        const heightFactor =
          (elementPositionTop + elementToDrag.getBoundingClientRect().height / 2) /
          (containerBottom - topPositionContainerWhenVisible);

        setFlexGrow(limitValueBetweenMaxAndMin(heightFactor));
      }
    },
    [containerResizable, elementToDrag, isMouseDownOnElementDrag, topPositionContainerWhenVisible],
  );

  return {
    flexGrow,
    isDragging,
    setIsMouseDownOnElementDrag,
    handleMouseMove,
  };
};
